import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Row,
  Col,
  Input,
  Select,
  Space,
  Typography,
  Spin,
  message,
  DatePicker,
} from "antd";
import dayjs from "dayjs";
import { SearchOutlined } from "@ant-design/icons";
import { ItemModel } from "../model/orders.ui";
import { orderApi } from "../api/orderApi";
import { MobilePullToRefreshList } from "../../../components/mobile/MobilePullToRefreshList";
import { OrderDesktopTable } from "../components/OrderDesktopTable";
import { OrderMobileCard } from "../components/OrderMobileCard";
import {
  getOrderNo,
  getCustomerName,
} from "../components/orderListHelpers";

const { RangePicker } = DatePicker;
const { Text } = Typography;

export interface OrderListPageProps {
  orders: ItemModel[];
  isDesktop: boolean;
  onSelectOrder?: (order: ItemModel) => void;
  onDeleteOrder?: (order: ItemModel) => Promise<void> | void;
  onRefresh?: () => Promise<void>;
  onLoadMore?: () => Promise<void>;
  onPageChange?: (page: number) => Promise<void>;
  isLoading?: boolean;
  isRefreshing?: boolean;
  isLoadingMore?: boolean;
  hasMore?: boolean;
  currentPage?: number;
  totalPages?: number;
  searchText?: string;
  onSearchChange?: (val: string) => void;
  statusFilter?: string;
  onStatusFilterChange?: (val: string) => void;
  dateRange?: [string, string] | null;
  onDateRangeChange?: (dates: [string, string] | null) => void;
}

export const OrderListPage: React.FC<OrderListPageProps> = ({
  orders = [],
  isDesktop,
  onSelectOrder,
  onDeleteOrder,
  onRefresh,
  onLoadMore,
  onPageChange,
  isLoading = false,
  isRefreshing = false,
  isLoadingMore = false,
  hasMore = false,
  currentPage = 1,
  totalPages = 1,
  searchText: propSearchText,
  onSearchChange,
  statusFilter: propStatusFilter,
  onStatusFilterChange,
  dateRange: propDateRange,
  onDateRangeChange,
}) => {
  const navigate = useNavigate();
  const [internalSearchText, setInternalSearchText] = useState("");
  const [internalStatusFilter, setInternalStatusFilter] = useState<string>("all");
  const [internalDateRange, setInternalDateRange] = useState<[string, string] | null>(null);

  const searchText =
    propSearchText !== undefined ? propSearchText : internalSearchText;
  const statusFilter =
    propStatusFilter !== undefined ? propStatusFilter : internalStatusFilter;
  const dateRange =
    propDateRange !== undefined ? propDateRange : internalDateRange;

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (onSearchChange) {
      onSearchChange(val);
    } else {
      setInternalSearchText(val);
    }
  };

  const handleStatusSelectChange = (val: string) => {
    if (onStatusFilterChange) {
      onStatusFilterChange(val);
    } else {
      setInternalStatusFilter(val);
    }
  };

  const handleDateRangeChange = (dates: any, dateStrings: [string, string]) => {
    const val: [string, string] | null = dates && dateStrings[0] && dateStrings[1] ? [dateStrings[0], dateStrings[1]] : null;
    if (onDateRangeChange) {
      onDateRangeChange(val);
    } else {
      setInternalDateRange(val);
    }
  };

  const [deletingId, setDeletingId] = useState<number | null>(null);

  const handleSelectOrder = (order: ItemModel) => {
    if (onSelectOrder) {
      onSelectOrder(order);
    } else {
      navigate(`/orders/${order.order_id}`, { state: { order } });
    }
  };

  const handleDeleteOrder = async (order: ItemModel) => {
    const orderId = order.order_id || order.id;
    if (!orderId) return;

    if (onDeleteOrder) {
      await onDeleteOrder(order);
    } else {
      try {
        setDeletingId(orderId);
        await orderApi.deleteOrder(orderId);
        message.success("注文を削除しました。"); // Xóa đơn hàng thành công!
        if (onRefresh) {
          await onRefresh();
        }
      } catch (error) {
        console.error("Failed to delete order:", error);
        message.error("注文の削除に失敗しました。"); // Xóa đơn hàng thất bại!
      } finally {
        setDeletingId(null);
      }
    }
  };

  const MOBILE_PAGE_SIZE = 10;
  const [mobileDisplayCount, setMobileDisplayCount] =
    useState<number>(MOBILE_PAGE_SIZE);
  const [internalRefreshing, setInternalRefreshing] = useState<boolean>(false);
  const [internalLoadingMore, setInternalLoadingMore] =
    useState<boolean>(false);

  const filteredOrders = orders.filter((order) => {
    const orderNo = getOrderNo(order);
    const customerName = getCustomerName(order);
    const matchesSearch =
      !searchText ||
      orderNo.toLowerCase().includes(searchText.toLowerCase()) ||
      customerName.toLowerCase().includes(searchText.toLowerCase());
    const matchesStatus =
      statusFilter === "all" ||
      order.status === statusFilter ||
      String(order.status).toLowerCase() === statusFilter.toLowerCase();
    let matchesDate = true;
    if (dateRange && dateRange[0] && dateRange[1]) {
      const dateToCheck = order.created_at || order.sales_date;
      if (dateToCheck) {
        const orderDateStr = dayjs(dateToCheck).format("YYYY-MM-DD");
        matchesDate = orderDateStr >= dateRange[0] && orderDateStr <= dateRange[1];
      }
    }
    return matchesSearch && matchesStatus && matchesDate;
  });

  const handleMobileRefresh = async () => {
    if (onRefresh) {
      await onRefresh();
    } else {
      setInternalRefreshing(true);
      await new Promise((resolve) => setTimeout(resolve, 600));
      setMobileDisplayCount(MOBILE_PAGE_SIZE);
      setInternalRefreshing(false);
    }
  };

  const handleMobileLoadMore = async () => {
    if (onLoadMore) {
      await onLoadMore();
    } else {
      if (internalLoadingMore || mobileDisplayCount >= filteredOrders.length)
        return;
      setInternalLoadingMore(true);
      await new Promise((resolve) => setTimeout(resolve, 500));
      setMobileDisplayCount((prev) =>
        Math.min(prev + MOBILE_PAGE_SIZE, filteredOrders.length)
      );
      setInternalLoadingMore(false);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        maxHeight: "100%",
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      {/* Search & Filter Toolbar */}
      <div style={{ flexShrink: 0, marginBottom: 16 }}>
        <Row gutter={[12, 12]}>
          <Col xs={24} sm={12} md={8}>
            <Input
              placeholder="注文番号または店舗名で検索..." /* Tìm kiếm theo mã đơn hàng hoặc tên cửa hàng... */
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={handleSearchInputChange}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              style={{ width: "100%" }}
              value={statusFilter}
              onChange={handleStatusSelectChange}
              options={[
                { value: "all", label: "すべてのステータス" /* Tất cả trạng thái */ },
                { value: "pending", label: "申請中" /* Đang yêu cầu / Chờ duyệt */ },
                {
                  value: "waiting_final_confirmation",
                  label: "最終確認待", /* Chờ xác nhận cuối cùng */
                },
                { value: "confirmed", label: "最終確定済み" /* Đã xác nhận */ },
              ]}
            />
          </Col>
          <Col xs={24} sm={24} md={10}>
            {isDesktop ? (
              <RangePicker
                style={{ width: "100%" }}
                placeholder={["開始日", "終了日"]} /* Ngày bắt đầu, Ngày kết thúc */
                format="YYYY-MM-DD"
                value={
                  dateRange && dateRange[0] && dateRange[1]
                    ? [dayjs(dateRange[0]), dayjs(dateRange[1])]
                    : null
                }
                onChange={handleDateRangeChange}
                allowClear
              />
            ) : (
              <Row gutter={8}>
                <Col span={12}>
                  <DatePicker
                    style={{ width: "100%" }}
                    placeholder="開始日" /* Ngày bắt đầu */
                    format="YYYY-MM-DD"
                    value={dateRange && dateRange[0] ? dayjs(dateRange[0]) : null}
                    onChange={(date) => {
                      const fromStr = date ? date.format("YYYY-MM-DD") : "";
                      const toStr = dateRange && dateRange[1] ? dateRange[1] : "";
                      const val: [string, string] | null =
                        !fromStr && !toStr ? null : [fromStr, toStr];
                      if (onDateRangeChange) onDateRangeChange(val);
                      else setInternalDateRange(val);
                    }}
                    allowClear
                  />
                </Col>
                <Col span={12}>
                  <DatePicker
                    style={{ width: "100%" }}
                    placeholder="終了日" /* Ngày kết thúc */
                    format="YYYY-MM-DD"
                    value={dateRange && dateRange[1] ? dayjs(dateRange[1]) : null}
                    onChange={(date) => {
                      const fromStr = dateRange && dateRange[0] ? dateRange[0] : "";
                      const toStr = date ? date.format("YYYY-MM-DD") : "";
                      const val: [string, string] | null =
                        !fromStr && !toStr ? null : [fromStr, toStr];
                      if (onDateRangeChange) onDateRangeChange(val);
                      else setInternalDateRange(val);
                    }}
                    allowClear
                  />
                </Col>
              </Row>
            )}
          </Col>
        </Row>
      </div>

      {/* Content area */}
      <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
        {isDesktop ? (
          <OrderDesktopTable
            orders={filteredOrders}
            isLoading={isLoading}
            currentPage={currentPage}
            totalPages={totalPages}
            isLoadingMore={isLoadingMore}
            onPageChange={onPageChange}
            onSelectOrder={handleSelectOrder}
            onDeleteOrder={handleDeleteOrder}
            deletingId={deletingId}
          />
        ) : isLoading ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <Spin size="large" >
              <span style={{ marginTop: 12, color: "#1890ff", fontWeight: 500 }}>
                注文一覧を読み込み中...{/* Đang tải danh sách đơn hàng... */}
              </span>
            </Spin>
          </div>
        ) : (
          <MobilePullToRefreshList
            onRefresh={handleMobileRefresh}
            onLoadMore={handleMobileLoadMore}
            isRefreshing={isRefreshing || internalRefreshing}
            isLoadingMore={isLoadingMore || internalLoadingMore}
            hasMore={
              onLoadMore ? hasMore : mobileDisplayCount < filteredOrders.length
            }
            totalItems={filteredOrders.length}
            currentItemsCount={filteredOrders.length}
          >
            <Space orientation="vertical" style={{ width: "100%" }} size="middle">
              {filteredOrders.length === 0 ? (
                <Text
                  type="secondary"
                  style={{
                    display: "block",
                    textAlign: "center",
                    padding: 20,
                  }}
                >
                  該当する注文がありません。{/* Không có đơn hàng phù hợp. */}
                </Text>
              ) : (
                (onLoadMore
                  ? filteredOrders
                  : filteredOrders.slice(0, mobileDisplayCount)
                ).map((order: ItemModel) => (
                  <OrderMobileCard
                    key={order.id}
                    order={order}
                    onSelect={handleSelectOrder}
                    onDelete={handleDeleteOrder}
                  />
                ))
              )}
            </Space>
          </MobilePullToRefreshList>
        )}
      </div>
    </div>
  );
};
